import React, { useState, useContext, createContext, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Layout } from '../../../components';
import { handleApi,formatMediumDate, capitalize} from '../../../utils/utils';

// --- MOCK CORE DEPENDENCIES (To resolve compilation errors) ---

// 1. Mock Context (Assuming basic user data and action management)
const AppContext = createContext({
  userData: { token: 'mock-token' },
  actionCount: 0,
  setActionCount: () => {},
});



// 3. Mock Toast Hook
const useToast = () => {
  const [toastMessage, setToastMessage] = useState({ show: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToastMessage({ show: true, message, type });
    setTimeout(() => setToastMessage(prev => ({ ...prev, show: false })), 3000);
  }, []);

  return { showToast, toastMessage };
};

// 4. Mock Utility Functions
const formatTwoDecimal = (num) => parseFloat(num || 0).toFixed(2);
const formatCurrency = (amount, currency = 'MK') => `${currency} ${formatTwoDecimal(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;


// 5. Mock AudioSpinner (Simple SVG loader)
const AudioSpinner = ({ size = 20, className = '' }) => (
  <svg className={`animate-spin ${className}`} style={{ width: `${size}px`, height: `${size}px` }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);



// --- ICON REPLACEMENTS (Using inline SVG instead of react-icons/md) ---
const BankIcon = (props) => <svg {...props} fill="currentColor" viewBox="0 0 24 24"><path d="M11 17h2v-6h-2v6zm-4 0h2v-6H7v6zm10 0h2v-6h-2v6zm2-10h-2V5h2v2zm-4 0h-2V5h2v2zm-4 0h-2V5h2v2zm-4 0H7V5h2v2zM3 21h18v-2H3v2zm9-17L3 9v1h18V9l-9-5z"/></svg>;
const CardIcon = (props) => <svg {...props} fill="currentColor" viewBox="0 0 24 24"><path d="M21 4H3C1.9 4 1 4.9 1 6v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H3V8h18v10zm-1-8H4V6h16v4z"/></svg>;
const MobileIcon = (props) => <svg {...props} fill="currentColor" viewBox="0 0 24 24"><path d="M15.5 1h-8C6.67 1 6 1.67 6 2.5v19c0 .83.67 1.5 1.5 1.5h8c.83 0 1.5-.67 1.5-1.5v-19c0-.83-.67-1.5-1.5-1.5zm-5 21c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm3-5H8V4h7v13z"/></svg>;
const CashIcon = (props) => <svg {...props} fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-1 14H5c-.55 0-1-.45-1-1v-4h18v4c0 .55-.45 1-1 1zm1-9H4V6h16v3z"/></svg>;
const ChevronUpIcon = (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>;
const ChevronDownIcon = (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
const CheckCircleIcon = (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const AlertIcon = (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>; // <-- Defined AlertIcon

// --- Configuration (Using the new mocked icons) ---
const PAYMENT_METHODS = [
  { id: 'bank_transfer', label: 'Bank Transfer', icon: <BankIcon size={20} /> },
  { id: 'credit_card', label: 'Credit Card', icon: <CardIcon size={20} /> },
  { id: 'mobile_money', label: 'Mobile Money', icon: <MobileIcon size={20} /> },
  { id: 'cash', label: 'Cash Payment', icon: <CashIcon size={20} /> },
  
];

// --- MOCK DATA FOR INSTALLMENTS ---
const MOCK_PAST_PAYMENTS = [
  { id: 'pay_001', date: '2025-09-01', method: 'Bank Transfer', amount: 50000.00, reference: 'BANK-TRF-0901' },
  { id: 'pay_002', date: '2025-09-15', method: 'Mobile Money', amount: 50000.00, reference: 'MPAMBA-088861541' },
];

// --- PDF Generation Logic (Adapted for rich data) ---

const handleDownloadReceipt = (receiptData, showToast) => {
  // Check if libraries are loaded by looking for them on the window object
  if (typeof window.html2canvas === 'undefined' || typeof window.jsPDF === 'undefined' || !window.jsPDF.default) {
      showToast('Receipt libraries still loading. Please wait 1 second and try again.', 'error');
      return;
  }

  showToast('Generating PDF receipt...', 'info');

  const jsPDF = window.jsPDF.default;
  const { id, invoiceRef, amount, date, methodLabel, reference, customerName, currencySymbol } = receiptData;

  // 1. Dynamic HTML content for the receipt capture (Uses inline styles for reliable capture)
  const receiptContent = `
      <div style="padding: 24px; width: 350px; font-family: ui-sans-serif, system-ui; background-color: #f9fafb; border: 2px solid #06b6d4; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
          <h3 style="text-align: center; color: #06b6d4; font-size: 1.75rem; font-weight: 800; margin-bottom: 16px; border-bottom: 3px double #e5e7eb; padding-bottom: 8px;">
              PAYMENT CONFIRMATION
          </h3>
          
          <div style="margin-bottom: 16px;">
              <p style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #4b5563; margin-bottom: 4px;">
                  <span>Invoice Ref:</span> 
                  <span style="font-weight: 600; color: #1f2937;">${invoiceRef}</span>
              </p>
              <p style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #4b5563; margin-bottom: 4px;">
                  <span>Customer:</span> 
                  <span style="font-weight: 600; color: #1f2937;">${customerName}</span>
              </p>
              <p style="display: flex; justify-content: space-between; font-size: 0.875rem; color: #4b5563; margin-bottom: 4px;">
                  <span>Transaction ID:</span> 
                  <span style="font-weight: 600; color: #1f2937;">${id.substring(4, 14)}</span>
              </p>
          </div>

          <div style="border-top: 1px dashed #ccc; padding-top: 12px; margin-bottom: 16px;">
              <p style="font-size: 1.125rem; font-weight: 700; color: #1f2937; margin-bottom: 8px;">Payment Details:</p>
              <p style="display: flex; justify-content: space-between; font-size: 0.95rem; color: #4b5563; margin-bottom: 4px;">
                  <span>Method:</span> 
                  <span style="font-weight: 600;">${methodLabel}</span>
              </p>
              <p style="display: flex; justify-content: space-between; font-size: 0.95rem; color: #4b5563; margin-bottom: 4px;">
                  <span>Reference:</span> 
                  <span style="font-weight: 600;">${reference}</span>
              </p>
              <p style="display: flex; justify-content: space-between; font-size: 0.95rem; color: #4b5563; margin-bottom: 4px;">
                  <span>Date:</span> 
                  <span style="font-weight: 600;">${new Date(date).toLocaleDateString()}</span>
              </p>
          </div>
          
          <div style="border-top: 2px solid #06b6d4; padding-top: 16px; margin-top: 16px;">
              <p style="text-align: right; font-size: 1.25rem; color: #4b5563; font-weight: 500;">TOTAL PAID</p>
              <p style="font-size: 2.5rem; font-weight: 900; color: #10b981; text-align: right;">
                  ${currencySymbol} ${formatTwoDecimal(amount)}
              </p>
          </div>
          
          <p style="text-align: center; margin-top: 24px; font-size: 0.75rem; color: #6b7280;">
              This receipt serves as proof of payment.
          </p>
      </div>
  `;

  // Create a temporary, off-screen element to capture
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = receiptContent;
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  document.body.appendChild(tempDiv);

  // Use html2canvas to capture
  window.html2canvas(tempDiv.firstChild, {
      scale: 3, // High scale for better PDF quality
      backgroundColor: '#ffffff'
  }).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      
      // Initialize jsPDF using the canvas dimensions
      const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
      });

      // Add the captured image to the PDF
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      
      // Save the file
      pdf.save(`receipt_${invoiceRef}_${id.substring(4, 10)}.pdf`);

      // Clean up
      document.body.removeChild(tempDiv);
      showToast(`Receipt downloaded successfully!`, 'success');
  }).catch(error => {
      console.error("PDF generation failed:", error);
      document.body.removeChild(tempDiv); // Clean up on error
      showToast('Error generating PDF. Check console for details.', 'error');
  });
};

const TOTAL_PAID_HISTORY = MOCK_PAST_PAYMENTS.reduce((sum, p) => sum + p.amount, 0); // 100000.00


// --- Sub-Components ---

// Futuristic Input Field
const ModernInput = ({ label, name, control, rules, type = 'text', readOnly = false, errors, currencySymbol }) => {
  return (
    <div className="relative z-0 w-full mb-6 group">
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <>
            <div className="flex items-center">
              {/* {currencySymbol && <span className="absolute left-0 text-cyan-300 pointer-events-none text-lg top-2 pl-1 font-extrabold">{currencySymbol}</span>} */}
              <input
                {...field}
                type={type}
                id={name}
                // Handle type='number' edge case for input field value (which is a string from react-hook-form)
                value={type === 'number' && typeof field.value === 'string' ? field.value : field.value || ''} 
                onChange={(e) => {
                    const value = e.target.value;
                    // Ensure number input only takes valid float values
                    if (type === 'number') {
                        // Allow empty string, or strings that look like valid numbers
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            field.onChange(value);
                        }
                    } else {
                        field.onChange(value);
                    }
                }}
                readOnly={readOnly}
                className={`
                  block py-2.5 px-0 w-full text-lg 
                  text-white bg-transparent border-0 border-b-2 
                  appearance-none focus:outline-none focus:ring-0 
                  peer transition-all duration-300 
                  ${readOnly ? 'border-gray-600 text-gray-400 cursor-not-allowed' : 'border-cyan-500 focus:border-cyan-300'}
                  ${currencySymbol ? 'pl-6' : 'pl-0'}
                `}
                placeholder=" "
                step={type === 'number' ? "0.01" : undefined} // Add step for currency precision
              />
            </div>
            <label
        htmlFor={name}
        className={`
          peer-focus:font-medium absolute text-xl text-cyan-400 
          duration-300 transform -translate-y-6 scale-75 top-3 z-10 
          origin-[0] peer-focus:start-0 
          peer-focus:text-cyan-300 peer-placeholder-shown:scale-100 
          peer-placeholder-shown:translate-y-0 peer-focus:scale-75 
          peer-focus:-translate-y-6 rtl:peer-focus:translate-x-full 
          rtl:peer-focus:left-auto
          ${readOnly ? 'text-gray-500' : ''}
        `}
      >
              {label}
            </label>
          </>
        )}
      />
      {errors?.[name] && (
        <p className="mt-2 text-xs text-red-400">{errors[name].message}</p>
      )}
    </div>
  );
};

// Past Installments Display Component
// Past Installments Display Component
const PastInstallments = ({ payments, currencySymbol }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (payments.length === 0) {
      return <p className="text-sm text-gray-500 italic mt-4">No past payments found for this invoice.</p>;
  }

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
      <div className="mt-6 border border-gray-700/50 rounded-lg overflow-hidden">
          <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex justify-between items-center p-4 bg-gray-700 hover:bg-gray-600 transition-colors duration-200"
          >
              {/* Left side text */}
              <span className="text-base font-semibold text-cyan-300">
                  {payments.length} Past Installment{payments.length > 1 ? 's' : ''} Recorded
              </span>
              
              {/* Right side: Grouped Amount and Chevron (Change Applied Here) */}
              <div className="flex items-center space-x-2">
                  <span className="text-xl font-bold text-green-300"> {formatCurrency(totalPaid, currencySymbol)}</span>
                  {isExpanded ? (<ChevronUpIcon className="w-5 h-5 text-cyan-300" />
                  ) : (<ChevronDownIcon className="w-5 h-5 text-cyan-300" />)}
              </div>
          </button>

          <div className={`transition-max-height duration-500 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[500px]' : 'max-h-0'}`}>
              <div className="p-4 bg-gray-800/60">
                  <ul className="space-y-3">
                      {payments.map((payment, index) => (
                          <li key={payment.id} className="p-3 border-b border-gray-700 last:border-b-0 bg-gray-900 rounded-md shadow-inner">
                              <div className="flex justify-between items-center">
                                  <p className="text-sm font-medium text-gray-200">
                                      {formatCurrency(payment.amount, currencySymbol)}
                                  </p>
                                  <p className="text-xs text-cyan-400 font-mono">
                                      {payment.date}
                                  </p>
                              </div>
                              <div className="flex justify-between items-center text-xs text-gray-400 mt-1">
                                  <span>Method: {payment.method}</span>
                                  <span className="truncate ml-2">Ref: {payment.reference}</span>
                              </div>
                          </li>
                      ))}
                  </ul>
              </div>
          </div>
      </div>
  );
};

// --- Main Component ---
const NewSalesPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { initialInvoice } = location.state || {};
  const [invoice, setInvoice] = useState(initialInvoice || {}); 
  const [pastPayments, setPastPayments] = useState(MOCK_PAST_PAYMENTS);
  const [lastSuccessfulPayment, setLastSuccessfulPayment] = useState(null); 
  
  const { userData, actionCount, setActionCount } = useContext(AppContext);
  const { showToast, toastMessage } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Default values based on invoice data
  const defaultAmount = invoice?.totalDiscountedPriceInclusive || 0;
  const currencySymbol = 'MK'; 

  const { control, handleSubmit, watch, formState: { errors } , setValue } = useForm({
    defaultValues: {
      paymentMethod: PAYMENT_METHODS[0].id,
      amountPaid: formatTwoDecimal(defaultAmount), // Initialize with the total amount due
      paymentDate: new Date().toISOString().substring(0, 10),
      transactionReference: '',
      notes: '',
    }
  });

  const selectedMethod = watch('paymentMethod');
  const amountPaid = watch('amountPaid');

  // Function to handle both state updates: Payment Method and Reference
  const handlePaymentMethodChange = (field, methodId) => {
    field.onChange(methodId); // 1. Update the paymentMethod field (required by react-hook-form)
    const newReference = `${capitalize(methodId)}`;// 2. Pre-populate the Reference field
    setValue('transactionReference', newReference, { shouldValidate: true });
  };
  

  // Calculate Balance
  // Ensure we parse the string value from the form field to a float for calculation
  const safeAmountPaid = parseFloat(amountPaid) || 0; 
  const balance = (parseFloat(defaultAmount) - safeAmountPaid).toFixed(2);
  const isOverpayment = parseFloat(balance) < 0;
  const isUnderpayment = parseFloat(balance) > 0;
  const isFullPayment = parseFloat(balance) === 0;

  const handlePaymentSubmit = async (data) => {
    setIsLoading(true);
    
    // Convert amountPaid back to a number
    const paymentAmount = parseFloat(data.amountPaid);
    
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      showToast('Payment amount must be a positive number.', 'error');
      setIsLoading(false);
      return;
    }

    const payload = {
      invoice_id: invoice.id,
      amount: paymentAmount,
      date: data.paymentDate,
      method: data.paymentMethod,
      reference: data.transactionReference,
      description: data.notes,
      // isFullPayment: isFullPayment, 
    };

    console.log('Payment Payload:', payload);

    try {
     
      const response = await handleApi('sales/payments', payload, userData, 'POST',{});

      if (response.status === 200 || response.status === 201) {
        showToast(response.data?.message || 'Payment recorded successfully!', 'success');
        
        setActionCount(prev => prev + 1);
        setTimeout(() => navigate(-1), 1500); 
      } else {
        showToast(response.data?.message || 'Error recording payment. Please try again.', 'error');
      }
    } catch (error) {
      console.error('API Error:', error);
      showToast(error.message || 'An unexpected error occurred during payment submission.', 'error');
    } finally {
      setIsLoading(false);
      //////////////////////////////////////////////////////////////////////////
    // --- START RECEIPT GENERATION DATA CAPTURE ---
  //   const receiptData = {
  //     id: 'TXN-' + Date.now(),
  //     invoiceRef: invoice.reference || invoice.id,
  //     amount: paymentAmount,
  //     date: data.paymentDate,
  //     methodLabel: PAYMENT_METHODS.find(m => m.id === data.paymentMethod)?.label || data.paymentMethod,
  //     reference: data.transactionReference,
  //     customerName: invoice?.partners?.name || 'N/A',
  //     currencySymbol: currencySymbol,
  // };
  // setLastSuccessfulPayment(receiptData);
  // // --- END RECEIPT GENERATION DATA CAPTURE ---

  // setActionCount(prev => prev + 1);
  // // setTimeout(() => navigate(-1), 1500); // Navigation removed for demo readability

    /////////////////////////////////////////////////////////////////////////
    }
  };

  if (!invoice || !invoice.id) {
    return (
      <Layout>
        <div className="p-10 text-center text-red-400 bg-gray-900 min-h-screen">
          Error: Invoice data not found. Please navigate from the Invoice Detail page.
          <button onClick={() => navigate(-1)} className="mt-4 block mx-auto text-cyan-400 hover:text-cyan-300">
            Go Back
          </button>
        </div>
      </Layout>
    );
  }

  return (
    // Provide a mock AppContext to make the component runnable
    <AppContext.Provider value={{ userData: { token: 'mock' }, actionCount: 0, setActionCount: () => {} }}>
      <Layout>
        <div className="min-h-screen text-white p-4 sm:p-8">
          
          {/* Toast Message Display */}
          {toastMessage.show && (
            <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md z-50
              ${toastMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {toastMessage.message}
            </div>
          )}

<div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center text-xl">
            RECORD PAYMENT
          </h1>
          <div className="flex gap-2">
            
            
            
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>


          

          {/* Main Payment Card - Futuristic Design */}
          <div className="bg-gray-800/80 backdrop-blur-sm rounded-3xl border border-cyan-700/50 shadow-[0_0_20px_rgba(6,182,212,0.3)] p-6 sm:p-10 mx-auto max-w-2xl">
            
            <form onSubmit={handleSubmit(handlePaymentSubmit)}>
              

               {/* INVOICE SUMMARY */}
              <div className="mb-4 p-4 bg-gray-900 border border-cyan-500/50 rounded-xl shadow-inner">
                  <p className="text-sm text-gray-400 mb-1">Invoice Reference:</p>
                  <p className="text-2xl font-black text-cyan-300">{invoice.reference || invoice.id}</p>

                  {/* Customer Name and Description Side-by-Side (Responsive Grid) */}
                  {/* Invoice Description and Customer Name Side-by-Side (Responsive Grid) */}
                <div className="flex justify-between mb-4">
                    {/* Invoice Description (Left) */}
                    <div>
                        <p className="text-xs text-gray-500">Invoice Description:</p>
                        <p className="text-sm italic text-gray-400">{invoice?.s_orders?.description}</p>
                    </div>

                    {/* Customer Name (Right) */}
                    <div>
                        <p className="text-xs text-gray-500">Customer Name:</p>
                        <p className="text-sm font-medium text-gray-300">{invoice?.partners?.name}</p>
                    </div>
                </div>
    
              
                  
                  <div className="flex justify-between items-center border-t border-cyan-600/50 pt-3">
                      <p className="text-lg font-semibold text-gray-300">Amount Due:</p>
                      <p className="text-3xl font-extrabold text-green-400">{formatCurrency(defaultAmount)}</p>
                  </div>

                  {/* PAST INSTALLMENTS SECTION */}
                  <PastInstallments payments={pastPayments} currencySymbol={currencySymbol} />
           
              </div>


              {/* PAYMENT METHOD SELECTION (GRID) */}
              <div className="flex flex-col sm:flex-row sm:items-start mb-2 border-b-2 mb-4 border-cyan-500">
              <h2 className="text-lg font-semibold text-cyan-400 mb-4  pb-2">Select Payment Method</h2>
                <div className="grid grid-cols-4 gap-4 mb-4">
                {PAYMENT_METHODS.map((method) => (
                  <Controller
                    key={method.id}
                    name="paymentMethod"
                    control={control}
                    render={({ field }) => (
                      <label 
                        htmlFor={method.id}
                        className={`
                          flex flex-col items-center justify-center p-1 text-xs
                          rounded-xl cursor-pointer transition-all duration-200 
                          shadow-md border-2
                          ${field.value === method.id 
                            ? 'bg-cyan-600/30 border-cyan-400 text-cyan-200 shadow-[0_0_15px_rgba(6,182,212,0.6)]'
                            : 'bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600'
                          }
                        `}
                      >
                        <input
                          {...field}
                          type="radio"
                          id={method.id}
                          value={method.id}
                          checked={field.value === method.id}
                          className="hidden"
                          onChange={() => handlePaymentMethodChange(field, method.id)}
                        />
                        <div className="mb-2 w-8 h-6">{method.icon}</div>
                        <span className="text-xs font-small text-center">{method.label}</span>
                      </label>
                    )}
                  />
                ))}
            </div>
              </div>
                

              {/* PAYMENT INPUTS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-6">
                
                {/* Amount Paid */}
                <ModernInput
                  label="Amount Paid"
                  name="amountPaid"
                  control={control}
                  type="number"
                  currencySymbol={currencySymbol}
                  errors={errors}
                  rules={{
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Must be greater than zero' }
                  }}
                />
                
                {/* Payment Date */}
                <ModernInput
                  label="Payment Date"
                  name="paymentDate"
                  control={control}
                  type="date"
                  errors={errors}
                  rules={{ required: 'Date is required' }}
                />

                {/* Transaction Reference */}
                <ModernInput
                  label="Transaction Reference"
                  name="transactionReference"
                  control={control}
                  errors={errors}
                  rules={{ required: 'Transaction reference is required' }}
                  value={`Transaction Reference (${capitalize(selectedMethod)})`}
                />

                {/* Notes */}
                <ModernInput
                  label="Notes (Optional)"
                  name="notes"
                  control={control}
                  errors={errors}
                />
              </div>

              <div className="mb-6 flex justify-start  ">
                    
                    
                    {isFullPayment ? (
                        // ELEGANT FULL PAYMENT STATUS (SUBTLE GLOW)
                        <div className="flex items-center text-green-300 transition-all duration-300 transform scale-105">
                            <CheckCircleIcon className="w-6 h-6 mr-2 shadow-green-500/50 drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
                            <span className="text-xl font-bold tracking-widest uppercase">
                                Paid in Full
                            </span>
                        </div>
                    ) : isOverpayment ? (
                        // OVERPAYMENT STATUS
                        <div className="flex items-center text-yellow-300">
                            <AlertIcon className="w-6 h-6 mr-2" />
                            <span className="text-xl font-bold uppercase">Overpaid</span>
                            <span className="ml-2 text-sm text-yellow-400">({formatCurrency(Math.abs(balance), currencySymbol)})</span>
                        </div>
                    ) : ( // isUnderpayment
                        // PARTIAL PAYMENT STATUS
                        <div className="flex items-center text-red-300">
                            <AlertIcon className="w-6 h-6 mr-2" />
                            <span className="text-xl font-bold uppercase">Balance Due</span>
                            <span className="ml-2 text-sm text-red-400">({formatCurrency(balance, currencySymbol)})</span>
                        </div>
                    )}
                </div>


              {/* ELEGANT BALANCE STATUS AND ACTION */}
              <div className="flex justify-end pt-4 border-t border-gray-700">
                {/* Refined Transaction Status Indicator */}
                                  
                  <button
                      type="submit"
                      disabled={isLoading || safeAmountPaid <= 0}
                      className=" p-2 rounded-lg font-extrabold uppercase tracking-wider 
                      transition-all duration-300 transform hover:scale-[1.01]
                      bg-cyan-600 text-white shadow-lg shadow-cyan-500/50 
                      disabled:bg-gray-600 disabled:shadow-none disabled:cursor-not-allowed"
                  >
                      {isLoading ? <AudioSpinner size={20} className="mx-auto" /> : 'Record Payment'}
                  </button>
              </div>

            </form>
          </div>
        </div>
      </Layout>
    </AppContext.Provider>
  );
};

// Export the component for use in your application's routing
export default NewSalesPayment;
